import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Check, 
  X, 
  Star, 
  Zap, 
  Users, 
  Shield, 
  ArrowRight,
  Crown,
  Sparkles
} from 'lucide-react';
import { useInView } from 'react-intersection-observer';

const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [ref1, inView1] = useInView({ threshold: 0.3, triggerOnce: true });
  const [ref2, inView2] = useInView({ threshold: 0.3, triggerOnce: true });

  const plans = [
    {
      name: 'Starter',
      description: 'Parfait pour les débutants',
      icon: Zap,
      color: 'from-sky-400 to-sky-600',
      price: { monthly: 29, yearly: 290 },
      features: [
        '5 projets actifs',
        'Cartographie basique',
        'Calculs de dimensionnement',
        'Export PDF standard',
        'Support email',
        'Mise à jour mensuelle'
      ],
      notIncluded: [
        'Intégration drone',
        'API météo avancée',
        'Support prioritaire',
        'Formation personnalisée'
      ],
      popular: false
    },
    {
      name: 'Professional',
      description: 'Pour les professionnels du secteur',
      icon: Crown,
      color: 'from-solar-400 to-solar-600',
      price: { monthly: 79, yearly: 790 },
      features: [
        'Projets illimités',
        'Cartographie avancée',
        'Intégration drone',
        'API météo temps réel',
        'Export PDF professionnel',
        'Support prioritaire',
        'Formation personnalisée',
        'Mise à jour hebdomadaire',
        'Gestion multi-utilisateurs'
      ],
      notIncluded: [
        'API personnalisée'
      ],
      popular: true
    },
    {
      name: 'Enterprise',
      description: 'Solutions sur mesure pour grandes entreprises',
      icon: Sparkles,
      color: 'from-purple-400 to-purple-600',
      price: { monthly: 199, yearly: 1990 },
      features: [
        'Tout du plan Professional',
        'API personnalisée',
        'Intégration ERP/CRM',
        'Support dédié 24/7',
        'Formation sur site',
        'Développements sur mesure',
        'SLA garanti',
        'Mise à jour en temps réel'
      ],
      notIncluded: [],
      popular: false
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: 'Sécurité maximale',
      description: 'Vos données sont protégées par un chiffrement de niveau bancaire'
    },
    {
      icon: Users,
      title: 'Support expert',
      description: 'Une équipe d\'experts photovoltaïques à votre service'
    },
    {
      icon: Zap,
      title: 'Mise à jour continue',
      description: 'Nouvelles fonctionnalités et améliorations régulières'
    }
  ];

  return (
    <div className="min-h-screen pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-6xl font-display font-bold text-white mb-6">
            Tarifs <span className="text-solar-400">Transparents</span>
          </h1>
          <p className="text-xl text-white/70 max-w-3xl mx-auto mb-8">
            Choisissez le plan qui correspond à vos besoins. Tous nos abonnements incluent 
            une période d'essai gratuit de 14 jours.
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center space-x-4">
            <span className={`text-sm ${billingCycle === 'monthly' ? 'text-white' : 'text-white/50'}`}>
              Mensuel
            </span>
            <motion.button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
              className="relative w-16 h-8 bg-white/20 rounded-full p-1"
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                className="w-6 h-6 bg-solar-400 rounded-full"
                animate={{ x: billingCycle === 'yearly' ? 32 : 0 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            </motion.button>
            <span className={`text-sm ${billingCycle === 'yearly' ? 'text-white' : 'text-white/50'}`}>
              Annuel
              <span className="ml-1 px-2 py-1 bg-green-500 text-white text-xs rounded-full">
                -20%
              </span>
            </span>
          </div>
        </motion.div>

        {/* Pricing Cards */}
        <div ref={ref1} className="grid md:grid-cols-3 gap-8 mb-16">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 50 }}
              animate={inView1 ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -10, scale: 1.02 }}
              className={`relative glass-effect rounded-3xl p-8 ${
                plan.popular ? 'ring-2 ring-solar-400' : ''
              }`}
            >
              {plan.popular && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={inView1 ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: 0.3 }}
                  className="absolute -top-4 left-1/2 transform -translate-x-1/2"
                >
                  <div className="bg-gradient-to-r from-solar-400 to-solar-600 text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center space-x-1">
                    <Star className="h-4 w-4" />
                    <span>Le plus populaire</span>
                  </div>
                </motion.div>
              )}

              <div className="text-center mb-8">
                <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r ${plan.color} flex items-center justify-center`}>
                  <plan.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-white/70 mb-6">{plan.description}</p>
                
                <div className="mb-6">
                  <span className="text-4xl font-bold text-white">
                    €{plan.price[billingCycle]}
                  </span>
                  <span className="text-white/50 ml-2">
                    /{billingCycle === 'monthly' ? 'mois' : 'an'}
                  </span>
                </div>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`w-full py-4 rounded-xl font-semibold transition-all duration-300 ${
                    plan.popular
                      ? 'bg-gradient-to-r from-solar-400 to-solar-600 text-white hover:shadow-lg hover:shadow-solar-400/25'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  Commencer l'essai gratuit
                  <ArrowRight className="inline ml-2 h-4 w-4" />
                </motion.button>
              </div>

              <div className="space-y-4">
                <h4 className="text-white font-semibold mb-4">Inclus :</h4>
                {plan.features.map((feature, featureIndex) => (
                  <motion.div
                    key={featureIndex}
                    initial={{ opacity: 0, x: -20 }}
                    animate={inView1 ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.4 + featureIndex * 0.05 }}
                    className="flex items-center space-x-3"
                  >
                    <Check className="h-5 w-5 text-green-400 flex-shrink-0" />
                    <span className="text-white/80 text-sm">{feature}</span>
                  </motion.div>
                ))}

                {plan.notIncluded.length > 0 && (
                  <>
                    <h4 className="text-white/50 font-semibold mb-4 mt-6">Non inclus :</h4>
                    {plan.notIncluded.map((feature, featureIndex) => (
                      <motion.div
                        key={featureIndex}
                        initial={{ opacity: 0, x: -20 }}
                        animate={inView1 ? { opacity: 1, x: 0 } : {}}
                        transition={{ delay: 0.6 + featureIndex * 0.05 }}
                        className="flex items-center space-x-3"
                      >
                        <X className="h-5 w-5 text-red-400 flex-shrink-0" />
                        <span className="text-white/50 text-sm">{feature}</span>
                      </motion.div>
                    ))}
                  </>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Benefits Section */}
        <div ref={ref2} className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={inView2 ? { opacity: 1, y: 0 } : {}}
            className="text-3xl md:text-4xl font-display font-bold text-white mb-12"
          >
            Pourquoi choisir <span className="text-solar-400">ProtoSunny</span> ?
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView2 ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="glass-effect rounded-2xl p-8 text-center card-hover"
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-r from-solar-400 to-solar-600 flex items-center justify-center">
                  <benefit.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">{benefit.title}</h3>
                <p className="text-white/70">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="glass-effect rounded-3xl p-8"
        >
          <h2 className="text-3xl font-display font-bold text-white text-center mb-8">
            Questions Fréquentes
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">
                Puis-je annuler à tout moment ?
              </h3>
              <p className="text-white/70">
                Oui, vous pouvez annuler votre abonnement à tout moment. Aucun engagement à long terme.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">
                L'essai gratuit est-il vraiment gratuit ?
              </h3>
              <p className="text-white/70">
                Absolument ! 14 jours d'essai gratuit sans carte bancaire requise.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">
                Mes données sont-elles sécurisées ?
              </h3>
              <p className="text-white/70">
                Vos données sont chiffrées et stockées de manière sécurisée selon les standards GDPR.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">
                Puis-je changer de plan ?
              </h3>
              <p className="text-white/70">
                Oui, vous pouvez changer de plan à tout moment. La facturation sera ajustée automatiquement.
              </p>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="glass-effect rounded-3xl p-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-6">
              Prêt à commencer ?
            </h2>
            <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto">
              Rejoignez des centaines de professionnels qui font confiance à ProtoSunny 
              pour leurs projets photovoltaïques.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/register" className="button-primary text-lg px-8 py-4">
                  Commencer l'essai gratuit
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/contact" className="button-secondary text-lg px-8 py-4">
                  Contacter l'équipe
                </Link>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Pricing; 